import { useEffect, useState } from 'react';
import PageHeader from '../../components/common/PageHeader';
import CopiarTexto from '../../components/common/CopiarTexto';
import Pill from '../../components/common/Pill';
import StatusBadge from '../../components/common/StatusBadge';
import * as endpointsApi from '../../api/endpoints';

/** Documentação viva do sistema, dentro do próprio sistema.
 *
 *  Escrita para quem não trabalha com integração todo dia: cada termo é explicado na primeira
 *  vez que aparece, e os exemplos de teste são montados com um endpoint real escolhido na
 *  própria página — copiar e colar tem que funcionar, não virar "adapte para o seu caso".
 *
 *  Segredo nenhum entra nos exemplos: onde entraria um token vai um marcador, porque esta
 *  página é visível para todos os perfis, inclusive os que veem credencial mascarada.
 */

const SECOES = [
  ['o-que-e', 'O que é este sistema'],
  ['glossario', 'Glossário'],
  ['como-funciona', 'Como funciona, do início ao fim'],
  ['telas', 'O que cada tela resolve'],
  ['nova-integracao', 'Criar uma integração nova'],
  ['testar', 'Testar um endpoint'],
  ['ler-a-tela', 'Entendendo o que você vê'],
  ['problemas', 'Problemas comuns'],
  ['perfis', 'Perfis de acesso'],
];

function Secao({ id, titulo, children }) {
  return (
    <section id={id} className="card-soft mb-3" style={{ scrollMarginTop: 20 }}>
      <div className="card-soft__header">
        <h2 className="card-soft__title" style={{ fontSize: 15 }}>
          {titulo}
        </h2>
      </div>
      <div className="card-soft__body">{children}</div>
    </section>
  );
}

function Bloco({ codigo, titulo }) {
  return (
    <div className="mb-3">
      <div className="d-flex align-items-center justify-content-between mb-1">
        {titulo && <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--wh-ink-2)' }}>{titulo}</span>}
        <CopiarTexto texto={codigo} rotulo="" titulo="Copiar" />
      </div>
      <pre className="codigo-bloco">{codigo}</pre>
    </div>
  );
}

function Termo({ nome, children }) {
  return (
    <div className="dado-linha" style={{ alignItems: 'flex-start' }}>
      <div className="dado-linha__rotulo" style={{ fontWeight: 700, color: 'var(--wh-ink)' }}>
        {nome}
      </div>
      <div className="dado-linha__valor">{children}</div>
    </div>
  );
}

export default function Documentacao() {
  const [endpoints, setEndpoints] = useState([]);
  const [escolhido, setEscolhido] = useState(null);

  useEffect(() => {
    endpointsApi
      .listar({ apenas_ativos: true })
      .then((r) => {
        setEndpoints(r.data);
        if (r.data.length) setEscolhido(r.data[0]);
      })
      .catch(() => {
        /* a página é útil mesmo sem a lista: os exemplos caem no endereço genérico */
      });
  }, []);

  const url = escolhido?.url_publica || `${window.location.origin}/hooks/SEU-SLUG`;
  const precisaToken = escolhido?.auth_tipo === 'token';
  const urlComToken = precisaToken ? `${url}?token=SEU_TOKEN` : url;

  const exemploPowerShell = `Invoke-RestMethod -Uri "${urlComToken}" \`
  -Method Post \`
  -ContentType "application/json" \`
  -Body '{"evento":"teste","id":123}'`;

  const exemploCurl = `curl -i -X POST "${urlComToken}" \\
  -H "Content-Type: application/json" \\
  -d '{"evento":"teste","id":123}'`;

  const exemploCabecalho = `curl -i -X POST "${url}" \\
  -H "Content-Type: application/json" \\
  -H "X-Webhook-Token: SEU_TOKEN" \\
  -d '{"evento":"teste","id":123}'`;

  return (
    <div>
      <PageHeader
        titulo="Documentação"
        subtitulo="O que o sistema faz, o que cada palavra significa e como testar uma integração"
      />

      <div className="card-soft mb-3">
        <div className="card-soft__body d-flex gap-2 flex-wrap">
          {SECOES.map(([id, rotulo]) => (
            <a key={id} href={`#${id}`} className="wh-btn wh-btn-outline wh-btn-sm">
              {rotulo}
            </a>
          ))}
        </div>
      </div>

      <Secao id="o-que-e" titulo="O que é este sistema">
        <p>
          Quando dois sistemas precisam conversar, existem duas formas: ou o nosso fica perguntando
          "tem novidade?" de tempos em tempos, ou o outro nos <strong>avisa</strong> assim que algo
          acontece. Esse aviso automático é o que se chama de <strong>webhook</strong>: o sistema do
          parceiro faz uma chamada para um endereço nosso toda vez que acontece algo relevante — uma
          venda, um pagamento confirmado, uma nota emitida.
        </p>
        <p>
          Este sistema é a <strong>caixa de entrada desses avisos</strong>. Ele recebe, guarda tudo o
          que chegou exatamente como chegou, e mostra numa tela. Ele não processa regra de negócio:
          quem faz alguma coisa com a informação é o sistema de destino. O papel dele é outro, e são
          três:
        </p>
        <ul style={{ fontSize: 13.5, lineHeight: 1.7 }}>
          <li>
            <strong>Descobrir</strong> — antes de escrever uma integração, apontar o parceiro para cá
            e ver o que ele manda de verdade, em vez de depender da documentação dele.
          </li>
          <li>
            <strong>Provar</strong> — quando alguém diz "mandei e vocês não receberam", a resposta
            deixa de ser discussão: está na tela, com data, hora e conteúdo. Ou não está.
          </li>
          <li>
            <strong>Recuperar</strong> — se o sistema de destino ficou fora do ar, os avisos continuam
            guardados aqui e podem ser reenviados.
          </li>
        </ul>
      </Secao>

      <Secao id="glossario" titulo="Glossário">
        <Termo nome="Webhook">
          O aviso automático que um sistema externo manda para o nosso quando algo acontece. Na
          prática é uma chamada pela internet carregando um texto com os dados do que ocorreu.
        </Termo>
        <Termo nome="Endpoint">
          Uma caixa de entrada com endereço próprio aqui dentro. Cada integração tem a sua: uma para
          as vendas do Tiny de um cliente, outra para as notas fiscais, outra para o carrinho
          abandonado da loja. É o que você cria na tela <strong>Endpoints</strong>.
        </Termo>
        <Termo nome="Slug">
          O pedaço final do endereço do endpoint, sem espaços nem acentos. No endereço{' '}
          <code className="wh-mono">/hooks/tiny-vendas</code>, o slug é{' '}
          <code className="wh-mono">tiny-vendas</code>. É ele que identifica para qual caixa de
          entrada a chamada vai.
        </Termo>
        <Termo nome="Evento">
          Uma chamada que chegou. Cada aviso recebido vira um evento, guardado inteiro e nunca
          alterado depois.
        </Termo>
        <Termo nome="Payload (ou corpo)">
          O conteúdo do aviso — os dados em si. Normalmente vem em JSON, um formato de texto com
          chaves e valores, tipo <code className="wh-mono">{'{"numero": "2591", "situacao": "faturado"}'}</code>.
        </Termo>
        <Termo nome="Cabeçalho (header)">
          Informações que acompanham a chamada, fora do conteúdo: quem está chamando, que formato
          está mandando, e muitas vezes a credencial. É o envelope; o payload é a carta.
        </Termo>
        <Termo nome="Credencial">
          A prova de que quem chamou é mesmo o parceiro. Este sistema aceita quatro modos:{' '}
          <strong>nenhuma</strong> (aceita qualquer chamada, bom para descobrir),{' '}
          <strong>token</strong> (uma senha fixa que o parceiro manda junto), <strong>Basic</strong>{' '}
          (usuário e senha) e <strong>HMAC</strong> (uma assinatura calculada sobre o conteúdo, a mais
          forte: prova também que o conteúdo não foi alterado no caminho).
        </Termo>
        <Termo nome="Cliente e Sistema">
          Duas etiquetas do endpoint: de quem é a integração e com o que ela conversa. Ficam em
          cadastro próprio porque o mesmo sistema atende vários clientes — dois clientes usando Tiny
          apontam para o mesmo sistema "Tiny", e renomear vale para todos de uma vez.
        </Termo>
        <Termo nome="Conteúdo repetido">
          Marca que aparece quando chega um aviso com conteúdo <em>idêntico</em> a outro já recebido
          no mesmo endpoint. Pode ser o parceiro reenviando, ou duas notificações diferentes que por
          acaso carregam a mesma informação. O sistema não tem como distinguir — por isso a marca diz
          o que ele sabe ("o conteúdo é igual"), não o que ele supõe.
        </Termo>
        <Termo nome="Quarentena">
          Quando alguém chama um endereço que não existe aqui, a chamada é recusada — mas fica
          registrada na tela <strong>Não identificadas</strong>. É como se descobre que um parceiro
          foi configurado com o endereço errado, em vez de ele falhar em silêncio.
        </Termo>
        <Termo nome="Encaminhamento e reenvio">
          O endpoint pode repassar automaticamente cada aviso recebido para outro sistema
          (encaminhamento). E qualquer evento guardado pode ser mandado de novo com um clique
          (reenvio), útil quando o destino estava fora do ar.
        </Termo>
        <Termo nome="Retenção">
          Por quantos dias os eventos daquele endpoint são guardados. Zero significa "para sempre". A
          limpeza não é automática: um administrador roda quando quiser.
        </Termo>
      </Secao>

      <Secao id="como-funciona" titulo="Como funciona, do início ao fim">
        <ol style={{ fontSize: 13.5, lineHeight: 1.8 }}>
          <li>
            Você cria um <strong>endpoint</strong> aqui e recebe um endereço, tipo{' '}
            <code className="wh-mono">{url}</code>.
          </li>
          <li>Esse endereço é colado na configuração de webhook do sistema parceiro.</li>
          <li>
            Quando acontece algo lá (uma venda, por exemplo), ele chama esse endereço mandando os
            dados.
          </li>
          <li>
            O sistema guarda <strong>tudo</strong>: conteúdo, cabeçalhos, de qual endereço de internet
            veio, o horário, o tamanho e quanto tempo levou.
          </li>
          <li>
            Ele tenta identificar sozinho de qual provedor veio e que tipo de evento é, e confere a
            credencial, se o endpoint tiver uma configurada.
          </li>
          <li>
            Responde <strong>200</strong> para o parceiro — o "recebi" que faz ele parar de tentar.
          </li>
          <li>Se o endpoint tiver encaminhamento ligado, repassa o aviso para o outro sistema.</li>
        </ol>
        <p style={{ fontSize: 13.5 }}>
          O princípio que guia tudo: <strong>nunca perder um aviso de origem conhecida</strong>.
          Conteúdo mal formado, método inesperado, credencial errada — tudo é guardado com o motivo
          anotado, porque um aviso descartado é uma pergunta que ninguém consegue responder depois.
        </p>
      </Secao>

      <Secao id="telas" titulo="O que cada tela resolve">
        <Termo nome="Dashboard">
          O panorama: quantos avisos chegaram por dia, quais endpoints e sistemas mais receberam, e os
          números que merecem atenção (recusados, credencial inválida, conteúdo repetido).
        </Termo>
        <Termo nome="Eventos">
          A lista de tudo que chegou, com filtros e — o mais útil no dia a dia —{' '}
          <strong>busca dentro do conteúdo</strong>. Procure pelo número do pedido e veja se o aviso
          daquele pedido chegou. Clique numa linha para ver o conteúdo inteiro.
        </Termo>
        <Termo nome="Endpoints">
          As caixas de entrada. Aqui você cria, copia o endereço para colar no parceiro, configura
          credencial e vê quantos avisos cada uma recebeu.
        </Termo>
        <Termo nome="Não identificadas">
          Chamadas para endereços que não existem aqui. Se um parceiro foi configurado errado, é nesta
          tela que ele aparece — e dá para criar o endpoint certo com um clique.
        </Termo>
        <Termo nome="Cadastros">
          Clientes e sistemas. Renomear aqui vale para todos os endpoints vinculados.
        </Termo>
        <Termo nome="Usuários e Auditoria">
          Quem tem acesso e o registro do que cada um fez. Só administradores veem a auditoria.
        </Termo>
      </Secao>

      <Secao id="nova-integracao" titulo="Criar uma integração nova">
        <ol style={{ fontSize: 13.5, lineHeight: 1.9 }}>
          <li>
            <strong>Endpoints → Novo endpoint.</strong> Dê um nome que explique a integração ("SENSE
            — Notificação de Venda"), escolha o cliente e o sistema, e deixe a autenticação em{' '}
            <strong>Nenhuma</strong> por enquanto.
          </li>
          <li>
            <strong>Copie o endereço</strong> com o botão ao lado dele e cole na tela de webhooks do
            sistema parceiro.
          </li>
          <li>
            <strong>Provoque um evento de teste lá</strong> — faça uma venda de teste, mude o status de
            um pedido. Ou use os comandos da seção seguinte.
          </li>
          <li>
            <strong>Abra Eventos.</strong> O aviso aparece em segundos, com o conteúdo real. É a partir
            daqui que se escreve a integração de verdade.
          </li>
          <li>
            <strong>Só então configure a credencial</strong>, se o parceiro oferecer uma. Ligar
            validação antes de ver o primeiro aviso chegar só produz evento marcado como inválido e
            confunde o diagnóstico.
          </li>
        </ol>
        <div className="alert alert-warning py-2 px-3" style={{ fontSize: 13 }}>
          O endereço de um endpoint é um combinado de longo prazo com o parceiro. Mudar o{' '}
          <strong>slug</strong> depois quebra a configuração que já foi feita lá — escolha com calma.
        </div>
      </Secao>

      <Secao id="testar" titulo="Testar um endpoint">
        <p style={{ fontSize: 13.5 }}>
          Serve para conferir se um endpoint recém-criado está recebendo, sem depender do parceiro.
          Escolha o endpoint e copie o comando pronto:
        </p>

        {endpoints.length > 0 && (
          <div className="mb-3" style={{ maxWidth: 420 }}>
            <label className="wh-label">Endpoint para os exemplos</label>
            <select
              className="wh-select"
              value={escolhido?.id_endpoint || ''}
              onChange={(e) => setEscolhido(endpoints.find((x) => x.id_endpoint === e.target.value))}
            >
              {endpoints.map((e) => (
                <option key={e.id_endpoint} value={e.id_endpoint}>
                  {e.nome}
                </option>
              ))}
            </select>
          </div>
        )}

        {precisaToken && (
          <div className="alert alert-warning py-2 px-3" style={{ fontSize: 13 }}>
            Este endpoint exige token. Troque <code>SEU_TOKEN</code> pelo valor real — ele está na tela
            de Endpoints, no botão do olho, ao lado do endereço. Não cole esse endereço completo em
            chamado nem em conversa: ele carrega a credencial.
          </div>
        )}

        <Bloco titulo="Windows — PowerShell (o terminal padrão do Windows)" codigo={exemploPowerShell} />
        <Bloco titulo="Linux, Mac ou Git Bash — curl" codigo={exemploCurl} />

        <p style={{ fontSize: 13.5, marginTop: 8 }}>
          <strong>O que esperar:</strong> resposta <code className="wh-mono">200</code> e, no caso do
          curl, um cabeçalho <code className="wh-mono">x-evento-id</code> com o código do evento. Em
          seguida ele aparece na tela de Eventos. Se vier{' '}
          <code className="wh-mono">404</code>, o endereço está errado ou o endpoint não existe; se
          vier <code className="wh-mono">410</code>, o endpoint está inativo.
        </p>

        {precisaToken && (
          <>
            <p style={{ fontSize: 13.5, marginTop: 16 }}>
              Se o parceiro conseguir mandar o token em cabeçalho (mais seguro que na URL, porque a URL
              aparece em registros de servidor), o formato é este:
            </p>
            <Bloco titulo="Token no cabeçalho" codigo={exemploCabecalho} />
          </>
        )}

        <p style={{ fontSize: 13.5, marginTop: 16 }}>
          Para testes mais completos — incluindo assinatura HMAC, que precisa de cálculo — existe uma
          coleção pronta do Postman no repositório do projeto, em{' '}
          <code className="wh-mono">webhook.postman_collection.json</code>. Importe, ajuste a variável{' '}
          <code className="wh-mono">base_url</code> e rode.
        </p>
      </Secao>

      <Secao id="ler-a-tela" titulo="Entendendo o que você vê">
        <p style={{ fontSize: 13.5 }}>As marcas coloridas na lista de eventos significam:</p>
        <div className="wh-table-wrap">
          <table className="wh-table">
            <thead>
              <tr>
                <th>Marca</th>
                <th>O que quer dizer</th>
                <th>O que fazer</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <StatusBadge status="recebido" />
                </td>
                <td>O aviso chegou e foi guardado.</td>
                <td>Nada. É o normal.</td>
              </tr>
              <tr>
                <td>
                  <StatusBadge status="rejeitado" />
                </td>
                <td>Chegou, foi guardado, mas respondemos erro ao parceiro (método, tamanho ou credencial).</td>
                <td>Abra o evento e veja o motivo da rejeição.</td>
              </tr>
              <tr>
                <td>
                  <StatusBadge status="valido" />
                </td>
                <td>A credencial conferiu.</td>
                <td>Nada.</td>
              </tr>
              <tr>
                <td>
                  <StatusBadge status="invalido" />
                </td>
                <td>O parceiro mandou uma credencial que não bate com a cadastrada.</td>
                <td>
                  Quase sempre é segredo desatualizado do lado dele — atualize lá, ou confira se o
                  segredo foi trocado aqui.
                </td>
              </tr>
              <tr>
                <td>
                  <StatusBadge status="ausente" />
                </td>
                <td>O endpoint espera credencial e o parceiro não mandou nenhuma.</td>
                <td>Configuração incompleta no parceiro.</td>
              </tr>
              <tr>
                <td>
                  <Pill tone="warning" icon="bi-files">
                    conteúdo repetido
                  </Pill>
                </td>
                <td>Conteúdo idêntico a outro aviso já recebido.</td>
                <td>
                  Se forem muitos e espaçados em minutos iguais, o parceiro pode não estar recebendo
                  nosso "recebi". Se forem em segundos, normalmente é o parceiro sendo repetitivo.
                </td>
              </tr>
              <tr>
                <td>
                  <Pill tone="warning" icon="bi-scissors">
                    truncado
                  </Pill>
                </td>
                <td>O conteúdo passou do tamanho máximo e foi cortado.</td>
                <td>Avise quem administra o sistema.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Secao>

      <Secao id="problemas" titulo="Problemas comuns">
        <div className="wh-table-wrap">
          <table className="wh-table">
            <thead>
              <tr>
                <th>Situação</th>
                <th>Provável causa</th>
                <th>O que fazer</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>"Configurei o parceiro e não chega nada"</td>
                <td>Endereço errado do lado dele</td>
                <td>
                  Olhe a tela <strong>Não identificadas</strong>: se ele estiver chamando um endereço
                  parecido, aparece lá — e dá para criar o endpoint certo na hora.
                </td>
              </tr>
              <tr>
                <td>Todos os eventos com credencial inválida</td>
                <td>Segredo trocado aqui e não atualizado lá</td>
                <td>Copie o valor atual na tela de Endpoints e atualize no painel do parceiro.</td>
              </tr>
              <tr>
                <td>O cliente jura que mandou</td>
                <td>Pode ter mandado para outro endereço, ou não ter mandado</td>
                <td>
                  Busque pelo número do pedido em <strong>Eventos</strong> (a busca olha dentro do
                  conteúdo) e confira também a tela Não identificadas.
                </td>
              </tr>
              <tr>
                <td>O sistema de destino ficou fora do ar</td>
                <td>Os avisos continuam guardados aqui</td>
                <td>Abra cada evento e use <strong>Reenviar</strong> quando o destino voltar.</td>
              </tr>
              <tr>
                <td>Resposta 429 ao testar vários endereços seguidos</td>
                <td>Proteção contra varredura</td>
                <td>São 20 tentativas por minuto em endereços inexistentes. Espere um minuto.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Secao>

      <Secao id="perfis" titulo="Perfis de acesso">
        <div className="wh-table-wrap">
          <table className="wh-table">
            <thead>
              <tr>
                <th>Perfil</th>
                <th>Pode</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Pill tone="neutral">Consulta</Pill>
                </td>
                <td>
                  Ver eventos e endpoints. Credenciais aparecem ocultas. É o perfil para quem dá
                  suporte e precisa responder "chegou ou não chegou".
                </td>
              </tr>
              <tr>
                <td>
                  <Pill tone="dark">Operador</Pill>
                </td>
                <td>
                  Tudo do consulta, mais criar e editar endpoints, cadastrar clientes e sistemas,
                  trocar credenciais e reenviar eventos.
                </td>
              </tr>
              <tr>
                <td>
                  <Pill tone="dark">Administrador</Pill>
                </td>
                <td>
                  Tudo, incluindo apagar eventos e endpoints, ver credenciais em claro, a auditoria e
                  a limpeza por retenção.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: 13, color: 'var(--wh-ink-2)', marginTop: 12, marginBottom: 0 }}>
          Precisa de acesso ou de outro perfil? Fale com um administrador — a criação de usuários é
          feita na tela Usuários.
        </p>
      </Secao>
    </div>
  );
}
